import {
  CheckCircleIcon,
  ChevronRightIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/solid';
import CircleDotted from 'assets/svg/icons/circle-dotted.svg';
import classNames from 'classnames';
import AIGradientBorder from 'components/atoms/AIGradientBorder.component';
import { Button } from 'components/atoms/Button';
import { motion } from 'motion/react';
import { type FC, type ReactNode } from 'react';

type Props = {
  label: string | ReactNode;
  onClick?: () => void;
  onCheck?: () => void;
  isDone: boolean;
  itemContainerClassNames?: string;
  hasCheckbox?: boolean;
  shouldHightlight?: boolean;
  isHidden?: boolean;
  onHelperClick?: () => void;
  disableUnchecking?: boolean;
  helper?: ReactNode;
  Icon?: FC<{ className?: string }>;
  className?: string;
  aiHighlight?: boolean;
};

const ActionListItem: FC<Props> = (props) => {
  const {
    label,
    onClick,
    isDone,
    itemContainerClassNames,
    hasCheckbox,
    isHidden,
    shouldHightlight,
    onCheck,
    onHelperClick,
    disableUnchecking,
    helper,
    Icon,
    className = '',
    aiHighlight = false,
  } = props;

  return (
    <div className={className}>
      <AIGradientBorder borderWidth={aiHighlight ? 2 : 0}>
        <motion.button
          type="button"
          initial={{ opacity: isHidden ? 0 : 1 }}
          animate={{
            opacity: isHidden ? 0 : 1,
          }}
          transition={{ duration: 0.1 }}
          onClick={onClick}
          className={classNames(
            'group bg-white border-b border-primary-100 hover:bg-primary-50 flex items-center z-20 justify-between transition-all opacity-100 duration-500 w-full px-4',
            itemContainerClassNames,
            {
              'cursor-default': !onClick,
              'rounded-lg': !!aiHighlight,
            },
          )}
        >
          {hasCheckbox ? (
            <div className="transition-all flex items-center space-x-2 pr-4">
              {isDone ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ ease: 'easeOut', duration: 0.5 }}
                  className={classNames(
                    'w-6 h-6 !mx-0 rounded-full flex items-center justify-center',
                  )}
                >
                  <CheckCircleIcon className="text-green-500 w-5 h-5 transition-all" />
                </motion.div>
              ) : (
                <div
                  className="w-6 h-6 "
                  style={{
                    backgroundImage: `url(${CircleDotted})`,
                    backgroundPosition: 'center center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
              )}
            </div>
          ) : null}
          <button
            type="button"
            onClick={!isDone ? onClick : () => null}
            className={classNames(
              'flex-1 flex items-center justify-between cursor-default py-4',
              {
                '!cursor-pointer': !!onClick && !isDone,
              },
            )}
          >
            <div
              className={classNames(
                'font-semibold text-primary text-left text-sm flex items-center',
                {
                  '!text-primary-600': isDone,
                },
              )}
            >
              <div className="flex items-center space-x-2">
                {Icon && !isDone ? (
                  <Icon className="w-6 h-6 text-primary" />
                ) : null}

                <span>{label}</span>
              </div>

              {!!helper && <div className="ml-1 flex">{helper}</div>}
              {onHelperClick ? (
                <div className="ml-1 flex items-center justify-center">
                  <Button
                    asDefault
                    onClick={(e) => {
                      e.stopPropagation();
                      onHelperClick?.();
                    }}
                  >
                    <QuestionMarkCircleIcon className="h-5 w-5 inline-block cursor-pointer text-primary-300 hover:text-primary-400 focus:text-primary-400" />
                  </Button>
                </div>
              ) : null}
            </div>

            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: isDone ? 0 : 1 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronRightIcon className="w-5 h-5 text-primary-300 transition-all duration-300" />
            </motion.div>
          </button>
        </motion.button>
      </AIGradientBorder>
    </div>
  );
};

export default ActionListItem;
